import { useState } from 'react';
import { Archive, Link2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useArchiveCertification,
  useCertifications,
  useCreateCertification,
  usePieces,
} from '../hooks/use-dossier-company';
import { PieceStatusBadge } from './piece-status-badge';
import {
  CERTIFICATION_TYPES_ORDER,
  CERTIFICATION_TYPE_LABELS,
} from './piece-types';
import type { CertificationType } from '../types/dossier-company.types';

export function ProfileCertificationsSection() {
  const { data, isLoading } = useCertifications();
  const create = useCreateCertification();
  const archive = useArchiveCertification();
  const { data: pieces } = usePieces();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmArchiveId, setConfirmArchiveId] = useState<number | null>(null);
  const [type, setType] = useState<CertificationType | ''>('');
  const [label, setLabel] = useState('');
  const [issuedAt, setIssuedAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [issuingBody, setIssuingBody] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [linkedDocumentId, setLinkedDocumentId] = useState<string>('');

  function reset() {
    setType('');
    setLabel('');
    setIssuedAt('');
    setExpiresAt('');
    setIssuingBody('');
    setReferenceNumber('');
    setLinkedDocumentId('');
  }

  async function handleSubmit() {
    if (!type) return;
    await create.mutateAsync({
      type,
      label: label || undefined,
      issuedAt: issuedAt || undefined,
      expiresAt: expiresAt || undefined,
      issuingBody: issuingBody || undefined,
      referenceNumber: referenceNumber || undefined,
      linkedDocumentId: linkedDocumentId ? parseInt(linkedDocumentId, 10) : undefined,
    });
    reset();
    setDialogOpen(false);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Référentiel des certifications avec date de validité. Renouvellement = archiver puis recréer.
        </p>
        <Button size="sm" onClick={() => setDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Ajouter une certification
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Délivrée</TableHead>
              <TableHead>Expire</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Organisme</TableHead>
              <TableHead>Pièce liée</TableHead>
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-4">
                  Chargement...
                </TableCell>
              </TableRow>
            )}
            {!isLoading && (!data || data.length === 0) && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-4">
                  Aucune certification.
                </TableCell>
              </TableRow>
            )}
            {data?.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">
                  {CERTIFICATION_TYPE_LABELS[c.type]}
                  {c.type === 'AUTRE' && c.label && (
                    <span className="text-muted-foreground ml-1">— {c.label}</span>
                  )}
                </TableCell>
                <TableCell>{formatDate(c.issuedAt)}</TableCell>
                <TableCell>{formatDate(c.expiresAt)}</TableCell>
                <TableCell>
                  <PieceStatusBadge status={c.status} />
                </TableCell>
                <TableCell className="text-sm">{c.issuingBody ?? '—'}</TableCell>
                <TableCell>
                  {c.linkedDocument ? (
                    <span className="text-sm flex items-center gap-1">
                      <Link2 className="h-3 w-3" />
                      {c.linkedDocument.filename.length > 24
                        ? `${c.linkedDocument.filename.substring(0, 24)}…`
                        : c.linkedDocument.filename}
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setConfirmArchiveId(c.id)}
                    title="Archiver"
                  >
                    <Archive className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) reset(); setDialogOpen(o); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajouter une certification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cert-type">Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as CertificationType)}>
                <SelectTrigger id="cert-type">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {CERTIFICATION_TYPES_ORDER.map((t) => (
                    <SelectItem key={t} value={t}>
                      {CERTIFICATION_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {type === 'AUTRE' && (
              <div className="space-y-2">
                <Label htmlFor="cert-label">Libellé</Label>
                <Input
                  id="cert-label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="ex. Certification XYZ"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cert-issued">Date délivrance</Label>
                <Input
                  id="cert-issued"
                  type="date"
                  value={issuedAt}
                  onChange={(e) => setIssuedAt(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cert-expires">Date expiration</Label>
                <Input
                  id="cert-expires"
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cert-issuing">Organisme certificateur</Label>
              <Input
                id="cert-issuing"
                value={issuingBody}
                onChange={(e) => setIssuingBody(e.target.value)}
                placeholder="ex. AFNOR Certification"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cert-ref">Numéro de référence</Label>
              <Input
                id="cert-ref"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cert-link">Pièce administrative liée (optionnel)</Label>
              <Select value={linkedDocumentId} onValueChange={setLinkedDocumentId}>
                <SelectTrigger id="cert-link">
                  <SelectValue placeholder="Aucune (peut être ajoutée plus tard)" />
                </SelectTrigger>
                <SelectContent>
                  {pieces?.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.filename}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSubmit} disabled={!type || create.isPending}>
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmArchiveId !== null}
        onOpenChange={(o) => !o && setConfirmArchiveId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archiver cette certification ?</AlertDialogTitle>
            <AlertDialogDescription>
              La certification est marquée archivée. Vous pouvez créer une nouvelle certification active du même type après archivage (renouvellement).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmArchiveId !== null) {
                  archive.mutate(confirmArchiveId);
                  setConfirmArchiveId(null);
                }
              }}
            >
              Archiver
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR');
}
